import {
    DialogContent,
    Box,
    IconButton,
    Typography,
    DialogActions,
    Grid,
    Badge,
    Paper,
    Card,
    CircularProgress,
    Button,
    Alert,
    Chip,
    InputAdornment,
    Tooltip
} from '@mui/material';
import { Control } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { CheckCircle, ContentCopy, Shuffle } from '@mui/icons-material';
import WSTextField, { TextType } from '../../../Components/TextBox/WSTextBox';
import WSButton from '../../../Components/Button/WSButton';
import { formatCardNumber, generateValidCardNumber, validateCardNumber } from '../../../helpers/cardHelpers';
import { cardTypes } from '.';
import DigitalCardCustomerForm from './DigitalCardCustomerForm';
import { callAPI, Method } from '../../../commonFunctions/CommonFunctions';
import WSHiddenField from '../../../Components/HiddenField/WSHiddenField';
import { Branches } from './DigitalCard';

interface CardFormProps {
    isLoading: boolean;
    cardNumberValue: String;
    cardType: 'Green' | 'Silver' | 'Gold';
    control: Control;
    setValue: (name: string, value: any) => void;
    watch: (name: string) => any;
    setCardType: React.Dispatch<React.SetStateAction<'Green' | 'Silver' | 'Gold'>>;
    onClose: () => void;
    handleGenerateCard: (data: any) => void;
    branches: Branches[];
}

const DigitalCardForm: React.FC<CardFormProps> = ({
    isLoading, cardNumberValue, cardType, control, branches,
    setValue, handleGenerateCard, setCardType, onClose }) => {

    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);

    const generateCardNumber = () => {
        setIsGenerating(true);

        // Simulate a small delay for better UX
        setTimeout(() => {
            const newCardNumber = generateValidCardNumber(cardType);
            setValue('cardNumber', formatCardNumber(newCardNumber));
            setIsGenerating(false);
        }, 500);
    };

    const copyToClipboard = () => {
        if (cardNumberValue) {
            navigator.clipboard.writeText(cardNumberValue.replace(/\s/g, ''));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <form onSubmit={handleGenerateCard} noValidate>
            <DialogContent sx={{
                mt: 3, p: 3, maxHeight: 'calc(100vh - 300px)', // Adjust based on header/footer height
                overflowY: 'auto',
            }}>
                {/* Customer Method Selection */}
                <Paper elevation={0} sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: '#e2e8f0',
                    backgroundColor: '#f8fafc'
                }}>
                    <Typography variant="subtitle1" gutterBottom sx={{
                        fontWeight: 600,
                        color: '#334155',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <Badge sx={{
                            background: "#3b82f6",
                            color: "white",
                            borderRadius: "50%",
                            width: 22,
                            height: 22,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 1.5,
                            fontSize: "0.7rem"
                        }}>
                            1
                        </Badge>
                        Select Card Type
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {cardTypes.map((type) => {
                            // const isDisabled = type.id === "Silver" || type.id === "Gold";
                            const isDisabled = false;
                            const isSelected = cardType === type.id;

                            return (
                                <Grid item xs={12} sm={4} key={type.id}>
                                    <Card
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            border: "2px solid",
                                            borderColor: isSelected ? "#3b82f6" : "#e2e8f0",
                                            backgroundColor: isDisabled
                                                ? "#f5f5f5"
                                                : isSelected
                                                    ? "rgba(59, 130, 246, 0.05)"
                                                    : "white",
                                            cursor: isDisabled ? "not-allowed" : "pointer",
                                            opacity: isDisabled ? 0.6 : 1,
                                            transition: "all 0.2s ease",
                                            height: "100%",
                                            "&:hover": {
                                                borderColor:
                                                    isDisabled
                                                        ? "#e2e8f0"
                                                        : isSelected
                                                            ? "#3b82f6"
                                                            : "#cbd5e1",
                                            },
                                        }}
                                        onClick={() => {
                                            if (!isDisabled) setCardType(type.id);
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 24,
                                                    borderRadius: 2,
                                                    background: type.color,
                                                    mr: 1.5,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: type.id === "Gold" ? "#333" : "white",
                                                    fontSize: "0.7rem",
                                                    fontWeight: "bold",
                                                    boxShadow:
                                                        type.id === "Silver"
                                                            ? "inset 0 0 2px rgba(0,0,0,0.15)"
                                                            : "none",
                                                }}
                                            >
                                                {type.id}
                                            </Box>
                                            {isDisabled && (
                                                <Chip
                                                    label="Coming Soon"
                                                    size="small"
                                                    sx={{
                                                        fontSize: "0.6rem",
                                                        height: "18px",
                                                        ml: 1,
                                                        backgroundColor: "#e2e8f0",
                                                        color: "#555",
                                                    }}
                                                />
                                            )}
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500, mb: 1, color: isDisabled ? "#9ca3af" : "inherit" }}
                                        >
                                            {type.label}
                                        </Typography>

                                        <Box sx={{ mt: 1 }}>
                                            {type.benefits.map((benefit, index) => (
                                                <Chip
                                                    key={index}
                                                    label={benefit}
                                                    size="small"
                                                    sx={{
                                                        mr: 0.5,
                                                        mb: 0.5,
                                                        fontSize: "0.65rem",
                                                        height: "20px",
                                                        backgroundColor: isDisabled
                                                            ? "rgba(0,0,0,0.05)"
                                                            : "rgba(59, 130, 246, 0.1)",
                                                        color: isDisabled ? "#9ca3af" : "inherit",
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        Selected: <strong>{cardType} Card</strong> - {cardTypes.find(t => t.id === cardType)?.benefits[0]}
                    </Alert>

                    <Box sx={{ mt: 2 }}>
                        <WSTextField
                            id="cardNumber"
                            label="Card Number"
                            name="cardNumber"
                            control={control}
                            // type={TextType.Number}
                            rules={{
                                required: 'Card number is required',
                                validate: value => validateCardNumber(value.replace(/\s/g, '')) || 'Invalid card number'
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white'
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Copy to clipboard">
                                            <IconButton
                                                onClick={copyToClipboard}
                                                disabled={!cardNumberValue}
                                                size="small"
                                                sx={{
                                                    mr: 1,
                                                    color: copied ? 'success.main' : 'inherit'
                                                }}
                                            >
                                                {copied ? <CheckCircle fontSize="small" /> : <ContentCopy fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Generate card number">
                                            <IconButton
                                                onClick={generateCardNumber}
                                                disabled={isGenerating}
                                                size="small"
                                            >
                                                {isGenerating ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    <Shuffle fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    <Box mt={2}>
                        {/* <WSTextField
                            id="openingPoints"
                            label="Opening Points"
                            name='openingPoints'
                            type={TextType.Number}
                            rules={{                                
                                min: {
                                    value: 0,
                                    message: 'Opening points cannot be negative'
                                }
                            }}
                            control={control}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white'
                                }
                            }}
                        /> */}

                        {branches.map((branch, index) => (
                            <Box key={branch._id} mt={2}>
                                <Typography variant="body2" mb={1}>{branch.name}</Typography>

                                <WSHiddenField control={control} name={`branches.${index}.branchId`} defaultValue={branch._id} />

                                <WSTextField
                                    id='openingPoints'
                                    name={`branches.${index}.openingPoints`}
                                    label="Opening Points"
                                    type={TextType.Number}
                                    rules={{
                                        min: {
                                            value: 0,
                                            message: 'Opening points cannot be negative'
                                        }
                                    }}
                                    control={control}
                                    defaultValue={0}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: 'white'
                                        }
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            onClick={generateCardNumber}
                            startIcon={isGenerating ? <CircularProgress size={16} /> : <Shuffle />}
                            disabled={isGenerating}
                            variant="outlined"
                            size="small"
                        >
                            {isGenerating ? 'Generating...' : 'Generate Card Number'}
                        </Button>
                    </Box>
                </Paper>

                <DigitalCardCustomerForm control={control} />

            </DialogContent>

            <DialogActions sx={{
                p: 3,
                pt: 0,
                borderTop: '1px solid #e2e8f0',
                background: '#f8fafc'
            }}>
                <WSButton
                    id="submit"
                    label={"Save"}
                    type="submit"
                    size="small"
                    loading={isLoading}
                />

                <WSButton
                    id="cancel"
                    label={"Cancel"}
                    type="button"
                    size="small"
                    className="ws-cancel"
                    onClick={onClose}
                />
            </DialogActions>
        </form>
    )
}

export default DigitalCardForm